import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tabs, Tab } from "@mui/material";
import { getBrokerPositionBook, getBrokerPositionBookColumns, postRepricePositions, postSpreadWindowOrder } from "../../api/brokerBooks";
import CustomTable from "../../components/CustomTable/CustomTable";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import BookSquareOffAddMoreFlow from "../../components/BookSquareOffAddMoreFlow/BookSquareOffAddMoreFlow";
import RepriceModal from "../../components/RepriceModal/RepriceModal";
import RollOverModal from "../../components/RollOverModal/RollOverModal";
import { buildRepricePositionsPayload } from "../../components/RepriceModal/repriceModalUtils";
import { buildSpreadWindowOrderPayload } from "../../components/RollOverModal/rollOverModalUtils";
import { extractColumnsConfig } from "../../utils/brokerBookColumns";
import { formatApiMessage } from "../../utils/formatApiMessage";
import { orderStatusNavigationStateFromResult } from "../../utils/normalizeOrderStatusResponse";
import styles from "./PositionBookPage.module.css";

function PositionBookPage() {
  const navigate = useNavigate();
  const [positionBook, setPositionBook] = useState([]);
  const [columnsConfig, setColumnsConfig] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isRepriceModalOpen, setIsRepriceModalOpen] = useState(false);
  const [isRollOverModalOpen, setIsRollOverModalOpen] = useState(false);
  const [repriceSubmitting, setRepriceSubmitting] = useState(false);
  const [rollOverSubmitting, setRollOverSubmitting] = useState(false);

  useEffect(() => {
    const fetchColumns = async () => {
      try {
        const response = await getBrokerPositionBookColumns();
        const columns = extractColumnsConfig(response);
        if (columns && columns.length > 0) {
          setColumnsConfig(columns);
        }
      } catch (err) {
        const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch columns configuration";
        console.error("Failed to fetch columns config:", errorMessage);
        setError(errorMessage);
      }
    };

    fetchColumns();
  }, []);

  useEffect(() => {
    const fetchPositionBook = async () => {
      setIsLoading(true);
      setError("");

      try {
        const data = await getBrokerPositionBook();
        setPositionBook(data["book_rows"] || []);
      } catch (err) {
        const errorMessage = err?.response?.data?.detail || err.message || "Failed to fetch position book data";
        setError(errorMessage);
        setPositionBook([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPositionBook();
  }, []);

  const handleRowSelect = useCallback((selectedIds, rowsData) => {
    setSelectedRows(selectedIds);
    setSelectedRowsData(rowsData || []);
  }, []);

  const handleReprice = () => {
    if (selectedRows.length > 0) {
      setIsRepriceModalOpen(true);
    }
  };

  const handleCloseRepriceModal = useCallback(() => {
    if (repriceSubmitting) return;
    setIsRepriceModalOpen(false);
  }, [repriceSubmitting]);

  const handleConfirmReprice = useCallback(
    async (modalPayload) => {
      const { initiateInstrumentsBySymbol = {}, ...repricePayload } = modalPayload || {};
      const apiPayload = buildRepricePositionsPayload(repricePayload, initiateInstrumentsBySymbol);

      if (!apiPayload.order_list?.length) {
        throw new Error("No reprice orders to place.");
      }

      setRepriceSubmitting(true);
      try {
        const result = await postRepricePositions(apiPayload);
        setIsRepriceModalOpen(false);
        navigate("/order-status", { state: orderStatusNavigationStateFromResult(result) });
      } catch (err) {
        const msg =
          formatApiMessage(err?.response?.data?.detail) ??
          formatApiMessage(err?.response?.data?.message) ??
          (typeof err?.message === "string" && err.message.trim() !== "" ? err.message : null) ??
          "Reprice failed.";
        setIsRepriceModalOpen(false);
        navigate("/order-status", { state: { squareOffError: msg } });
        throw err;
      } finally {
        setRepriceSubmitting(false);
      }
    },
    [navigate]
  );

  const handleRollOver = () => {
    if (selectedRows.length > 0) {
      setIsRollOverModalOpen(true);
    }
  };

  const handleCloseRollOverModal = useCallback(() => {
    if (rollOverSubmitting) return;
    setIsRollOverModalOpen(false);
  }, [rollOverSubmitting]);

  const handleConfirmRollOver = useCallback(
    async (modalPayload) => {
      const apiPayload = buildSpreadWindowOrderPayload(modalPayload);
      if (apiPayload.length === 0) {
        throw new Error("No roll over orders to place.");
      }

      setRollOverSubmitting(true);
      try {
        const result = await postSpreadWindowOrder(apiPayload);
        setIsRollOverModalOpen(false);
        navigate("/order-status", { state: orderStatusNavigationStateFromResult(result) });
      } catch (err) {
        const msg =
          formatApiMessage(err?.response?.data?.detail) ??
          formatApiMessage(err?.response?.data?.message) ??
          (typeof err?.message === "string" && err.message.trim() !== "" ? err.message : null) ??
          "Roll over failed.";
        setIsRollOverModalOpen(false);
        navigate("/order-status", { state: { squareOffError: msg } });
        throw err;
      } finally {
        setRollOverSubmitting(false);
      }
    },
    [navigate]
  );

  const handleExport = useCallback(() => {}, []);

  const toolbarPrefix = (
    <>
      <Button
        variant="outlined"
        className={styles["action-button"]}
        onClick={handleReprice}
        disabled={selectedRows.length === 0}
        startIcon={<GlobalIcon iconName="DoubleLeftArrowIcon" />}
      >
        Reprice
      </Button>
      <Button
        variant="outlined"
        className={styles["action-button"]}
        onClick={handleRollOver}
        disabled={selectedRows.length === 0}
        startIcon={<GlobalIcon iconName="RefreshIcon" />}
      >
        Roll Over
      </Button>
    </>
  );

  return (
    <div className={styles["container"]}>
      <div className={styles["primary-tabs-container"]}>
        <Tabs value="positions" className={styles["primary-tabs"]}>
          <Tab
            icon={<GlobalIcon iconName="HoldingBookIcon" className={styles["tab-icon"]} />}
            iconPosition="start"
            label="Positions"
            value="positions"
            className={styles["primary-tab"]}
            disableRipple
          />
        </Tabs>
      </div>

      <div className={styles["content-wrapper"]}>
        <BookSquareOffAddMoreFlow
          bookType="position"
          selectedRowsData={selectedRowsData}
          selectedCount={selectedRows.length}
          buttonClassName={styles["action-button"]}
          containerClassName={styles["action-buttons-container"]}
          toolbarPrefix={toolbarPrefix}
        />

        {error && <div className={styles["error-message"]}>{error}</div>}

        <div className={styles["table-wrapper"]}>
          {isLoading ? (
            <div className={styles["loading-message"]}>Loading...</div>
          ) : (
            <CustomTable
              columns={columnsConfig}
              data={positionBook}
              onRowSelect={handleRowSelect}
              selectedRows={selectedRows}
              rowKey="key"
              enableSearch={true}
              enableSorting={true}
              enableSelection={true}
              enableShowEntries={true}
              enableFilter={false}
              enableExport={true}
              enablePagination={true}
              defaultEntriesPerPage={100}
              searchPlaceholder="Search"
              onExport={handleExport}
              exportType="Positions"
              columnsWithDropdown={["broker_client_code", "exchange_trading_symbol"]}
            />
          )}
        </div>
      </div>

      <RepriceModal
        open={isRepriceModalOpen}
        onClose={handleCloseRepriceModal}
        onConfirm={handleConfirmReprice}
        selectedRows={selectedRowsData}
        submitting={repriceSubmitting}
      />

      <RollOverModal
        open={isRollOverModalOpen}
        onClose={handleCloseRollOverModal}
        onConfirm={handleConfirmRollOver}
        selectedRows={selectedRowsData}
        submitting={rollOverSubmitting}
      />
    </div>
  );
}

export default PositionBookPage;
